import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { SmartGridComponent, ColDef, GridConfig } from 'app/shared/components/smart-grid';
import { MockOrder, MOCK_ORDERS } from '../mock-data/mock-orders.data';

/**
 * Export Example
 * Demonstrates Excel and CSV export functionality
 */
@Component({
  selector: 'app-export-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule, MatMenuModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Exportación (Excel / CSV)</h2>
        <p class="text-gray-600 mb-2">
          Exporta los datos del grid a archivos Excel (.xlsx) o CSV (.csv) con un solo clic.
        </p>
        <div class="flex gap-2 flex-wrap mb-4">
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            📊 Excel Export
          </span>
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            📄 CSV Export
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            🎨 Styled Headers
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            📐 Auto-sized Columns
          </span>
        </div>

        <!-- Export Actions -->
        <div class="flex items-center gap-4 mb-4 flex-wrap">
          <button mat-raised-button color="primary" (click)="exportToExcel()">
            <mat-icon>file_download</mat-icon>
            Exportar a Excel
          </button>

          <button mat-raised-button color="accent" (click)="exportToCsv()">
            <mat-icon>description</mat-icon>
            Exportar a CSV
          </button>

          <button mat-button [matMenuTriggerFor]="exportMenu">
            <mat-icon>more_vert</mat-icon>
            Más opciones
          </button>

          <mat-menu #exportMenu="matMenu">
            <button mat-menu-item (click)="exportToExcelAllData()">
              <mat-icon>cloud_download</mat-icon>
              <span>Exportar TODOS los datos (Excel)</span>
            </button>
            <button mat-menu-item (click)="exportToCsvAllData()">
              <mat-icon>cloud_download</mat-icon>
              <span>Exportar TODOS los datos (CSV)</span>
            </button>
            <button mat-menu-item (click)="exportSelectedToExcel()" [disabled]="!hasSelection()">
              <mat-icon>check_circle</mat-icon>
              <span>Exportar seleccionados (Excel)</span>
            </button>
            <button mat-menu-item (click)="exportCustomColumns()">
              <mat-icon>view_column</mat-icon>
              <span>Exportar columnas específicas</span>
            </button>
          </mat-menu>
        </div>

        <div class="p-3 bg-blue-50 dark:bg-blue-900 rounded">
          <p class="text-sm">
            <strong>Datos actuales:</strong> {{ orders.length }} registros |
            <strong>Filtrados:</strong> {{ getFilteredCount() }} |
            <strong>Seleccionados:</strong> {{ getSelectedCount() }}
          </p>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          #grid
          [data]="orders"
          [columnDefs]="columnDefs"
          [config]="gridConfig"
          (selectionChanged)="onSelectionChanged($event)">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">📊 Características de exportación:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li><strong>Excel (.xlsx):</strong> Formato profesional con headers estilizados, bordes y columnas auto-ajustadas</li>
          <li><strong>CSV (.csv):</strong> Formato universal compatible con cualquier software (Excel, Google Sheets, etc.)</li>
          <li><strong>Exportar datos filtrados:</strong> Solo exporta las filas visibles después de aplicar filtros</li>
          <li><strong>Exportar todos los datos:</strong> Exporta el dataset completo sin aplicar filtros</li>
          <li><strong>Exportar selección:</strong> Solo exporta las filas seleccionadas con checkbox</li>
          <li><strong>Columnas específicas:</strong> Elige qué columnas exportar</li>
          <li><strong>Value Formatters:</strong> Respeta los formateadores de valores (ej: formato de moneda)</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900 rounded">
        <h3 class="font-semibold mb-2">🎯 Casos de uso:</h3>
        <ul class="text-sm space-y-2 list-disc list-inside">
          <li><strong>Reportes:</strong> Genera reportes descargables de datos filtrados</li>
          <li><strong>Análisis offline:</strong> Permite análisis de datos en Excel o herramientas BI</li>
          <li><strong>Compartir datos:</strong> Exporta y comparte datos con otros departamentos</li>
          <li><strong>Backup:</strong> Respalda snapshots de datos importantes</li>
          <li><strong>Migración:</strong> Facilita migración de datos entre sistemas</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📝 Código de este ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{exampleCode}}</code></pre>
      </div>

      <div class="mt-4 p-4 bg-red-50 dark:bg-red-900 rounded">
        <h3 class="font-semibold mb-2">💡 Tips:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li>Los archivos Excel incluyen estilos profesionales automáticamente</li>
          <li>Los archivos CSV usan UTF-8 BOM para compatibilidad con Excel</li>
          <li>Las columnas ocultas NO se exportan (solo las visibles)</li>
          <li>Los filtros se aplican antes de exportar (exporta solo datos visibles)</li>
        </ul>
      </div>
    </div>
  `,
})
export class ExportExampleComponent {
  @ViewChild('grid') grid!: SmartGridComponent<MockOrder>;

  orders: MockOrder[] = MOCK_ORDERS;
  selectedCount: number = 0;

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nro. Documento',
      width: 180,
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 300,
    },
    {
      field: 'class.name',
      headerName: 'Clase',
      width: 150,
    },
    {
      field: 'soldTo.address.city',
      headerName: 'Ciudad',
      width: 150,
    },
    {
      field: 'soldTo.address.country',
      headerName: 'País',
      width: 120,
    },
    {
      field: 'subTotal',
      headerName: 'Subtotal',
      width: 130,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'totalTaxes',
      headerName: 'Impuesto',
      width: 130,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'totalDiscounts',
      headerName: 'Descuento',
      width: 130,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 150,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    pagination: {
      enabled: true,
      pageSize: 20,
    },
    sorting: {
      enabled: true,
      multiColumn: true,
    },
    columnFilters: {
      enabled: true,
    },
    search: {
      enabled: true,
      showSearchInput: true,
    },
    selection: {
      enabled: true,
      mode: 'multiple',
      showCheckboxColumn: true,
    },
    styling: {
      maxHeight: '500px',
    },
  };

  /**
   * Export filtered/displayed data to Excel
   */
  exportToExcel(): void {
    this.grid.api.exportToExcel('orders-export');
  }

  /**
   * Export filtered/displayed data to CSV
   */
  exportToCsv(): void {
    this.grid.api.exportToCsv('orders-export');
  }

  /**
   * Export ALL data (ignore filters) to Excel
   */
  exportToExcelAllData(): void {
    this.grid.api.exportToExcel('orders-all-data', {
      dataScope: 'all',
    });
  }

  /**
   * Export ALL data (ignore filters) to CSV
   */
  exportToCsvAllData(): void {
    this.grid.api.exportToCsv('orders-all-data', {
      dataScope: 'all',
    });
  }

  /**
   * Export selected rows to Excel
   */
  exportSelectedToExcel(): void {
    const selected = this.grid.api.getSelectedRows();
    if (selected.length === 0) {
      alert('No hay filas seleccionadas');
      return;
    }

    // Temporarily set data to selected rows for export
    const originalData = this.grid.api.getData();
    this.grid.api.setData(selected);
    this.grid.api.exportToExcel('orders-selected');
    this.grid.api.setData(originalData);
  }

  /**
   * Export specific columns only
   */
  exportCustomColumns(): void {
    this.grid.api.exportToExcel('orders-custom-columns', {
      columns: ['serial', 'soldTo.name', 'total'],
    });
  }

  /**
   * Handle selection changed
   */
  onSelectionChanged(event: any): void {
    this.selectedCount = event.selectedRows.length;
  }

  /**
   * Check if has selection
   */
  hasSelection(): boolean {
    return this.selectedCount > 0;
  }

  /**
   * Get filtered row count
   */
  getFilteredCount(): number {
    return this.grid?.api?.getDisplayedRowCount() ?? this.orders.length;
  }

  /**
   * Get selected row count
   */
  getSelectedCount(): number {
    return this.selectedCount;
  }

  exampleCode = `// Component Code
import { ViewChild } from '@angular/core';
import { SmartGridComponent } from 'app/shared/components/smart-grid';

export class ExportExampleComponent {
  @ViewChild('grid') grid!: SmartGridComponent<MockOrder>;

  // Export filtered/displayed data to Excel
  exportToExcel(): void {
    this.grid.api.exportToExcel('filename');
  }

  // Export filtered/displayed data to CSV
  exportToCsv(): void {
    this.grid.api.exportToCsv('filename');
  }

  // Export ALL data (ignore filters)
  exportAllData(): void {
    this.grid.api.exportToExcel('filename', {
      dataScope: 'all'  // 'all' | 'displayed' | 'selected'
    });
  }

  // Export specific columns only
  exportCustomColumns(): void {
    this.grid.api.exportToExcel('filename', {
      columns: ['field1', 'field2', 'field3']
    });
  }

  // Export with custom processing
  exportWithProcessing(): void {
    this.grid.api.exportToExcel('filename', {
      processValue: (value, field) => {
        // Custom value transformation
        return value;
      },
      processHeader: (headerName, field) => {
        // Custom header transformation
        return headerName.toUpperCase();
      }
    });
  }
}

// Template
<app-smart-grid #grid
  [data]="orders"
  [columnDefs]="columnDefs"
  [config]="gridConfig">
</app-smart-grid>

<button (click)="exportToExcel()">Export to Excel</button>
<button (click)="exportToCsv()">Export to CSV</button>

// Features:
// - Excel: Professional styling, borders, auto-sized columns
// - CSV: UTF-8 BOM encoding for Excel compatibility
// - Respects value formatters (currency, dates, etc.)
// - Only exports visible columns
// - Can export filtered, all, or selected data
// - Customizable column selection
// - Custom value and header processing`;
}
